<script setup lang="ts">
import { storeToRefs } from 'pinia'

import InfluencerProfileBanner from '~/components/influencer/profile/Banner.vue'
import InfluencerProfileHeaderNavigation from '~/components/influencer/profile/header/Navigation.vue'

import { useCuratorContestsInfos } from '~/composables/useCuratorContestsInfos'

import { useMiscResizeStore } from '~/stores/miscResize'
import { useTagStore } from '~/stores/tag'
import { useUserInfluencerStore } from '~/stores/userInfluencer'

import { Breakpoints } from '~/enums/breakpoints'

import type Tag from '~/entities/tag'
import type { Influencer } from '~/types/influencer'

type Props = {
  influencer: Influencer
  userIsOwner: boolean
  hasInteraction: boolean
  showAbout: boolean
  showInteraction: boolean
}

type Emits = {
  toggle: []
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const { t } = useI18n()
const { curatorIsContest } = useCuratorContestsInfos(props.influencer)
const { GET_TAG_FROM_ID, GET_TAGS_FROM_IDS } = useTagStore()
const { SCREEN_WIDTH } = storeToRefs(useMiscResizeStore())

const displayBucketList = ref(false)
const banner = ref<InstanceType<typeof InfluencerProfileBanner> | null>(null)

const { picture } = useGetUserProfilePicture()

const isContest = computed(() => curatorIsContest(props.influencer))

const kinds = computed(() => {
  if (isContest.value) return t(`tags.influencer_kind.springboard`)

  return GET_TAGS_FROM_IDS(
    props.influencer.tags?.identity.influencer_kind ?? [],
  )
    .map((kindTag: Tag) => {
      return t(`tags.influencer_kind.${kindTag.name}`)
    })
    .join(', ')
})

const country = computed(() => {
  if (!props.influencer.tags?.identity.country) return undefined

  const tag = GET_TAG_FROM_ID(props.influencer.tags?.identity.country[0] ?? 0)
  if (!tag) return undefined

  return t(`tags.country.${tag.name}`)
})

function editBanner() {
  banner.value?.addPicture()
}

function toggle() {
  if (props.hasInteraction) emit('toggle')
}

// inline composable for the profile picture logic
function useGetUserProfilePicture() {
  const { timestamp } = useGetServerSyncedTimestamp('header-profile-image')
  const { getInfProfilePictureForSlug } = useGetProfilePicture()
  const { has_profile_picture: HAS_PROFILE_PICTURE } = storeToRefs(
    useUserInfluencerStore(),
  )

  const picture = computed(() => {
    return getInfProfilePictureForSlug(
      props.influencer.slug,
      { target: 'profile_picture', size: '400_400' },
      timestamp.value,
    )
  })

  watch(HAS_PROFILE_PICTURE, (newValue) => {
    if (!newValue) return
    timestamp.value++
  })

  return {
    picture,
  }
}
</script>

<template>
  <article
    class="tw-relative tw-flex tw-w-full tw-flex-col"
    :class="{ isContest }"
  >
    <div
      class="tw-relative tw-flex tw-w-full tw-items-center tw-justify-center lg:tw-min-h-[200px] lg:tw-items-end lg:tw-justify-normal"
    >
      <InfluencerProfileBanner
        ref="banner"
        class="tw-absolute tw-size-full tw-bg-orange-500"
        :influencer="influencer"
        :user-is-owner="userIsOwner"
      />
      <LazyInfluencerProfileHeaderInfoMobile
        v-if="SCREEN_WIDTH < Breakpoints.lg"
        :influencer="influencer"
        :user-is-owner="userIsOwner"
        :img-src="picture"
        :kinds="kinds"
        :country="country"
        :is-contest="isContest"
        @edit-banner="editBanner"
      />
      <LazyInfluencerProfileHeaderInfoDesktop
        v-else
        :influencer="influencer"
        :user-is-owner="userIsOwner"
        :img-src="picture"
        :kinds="kinds"
        :country="country"
        :is-contest="isContest"
        @edit-banner="editBanner"
      />
    </div>
    <InfluencerProfileHeaderNavigation
      v-model="displayBucketList"
      :influencer="influencer"
      :user-is-owner="userIsOwner"
      :show-about="showAbout"
      :has-interaction="hasInteraction"
      :show-interaction="showInteraction"
      @toggle="toggle"
    />
  </article>
</template>
